/**
 * Last-resort reactive recovery when a lazy-loaded chunk fails (e.g. a superseded
 * chunk pruned after the 7-day post-deploy retention window, or a MIME mismatch).
 * Primary update paths:
 * no-cache HTML document at the CDN + version.json notify-to-reload prompt.
 */
export const CHUNK_RECOVERY_FLAG_KEY = 'equip-track-chunk-recovered-at';

const CHUNK_MESSAGE_PATTERNS: readonly RegExp[] = [
  /Loading chunk [^ ]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Failed to load module script/i,
];

function getErrorName(error: unknown): string | undefined {
  if (
    error !== null &&
    typeof error === 'object' &&
    'name' in error &&
    typeof (error as { name: unknown }).name === 'string'
  ) {
    return (error as { name: string }).name;
  }
  return undefined;
}

function getErrorMessage(error: unknown): string | undefined {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (
    error !== null &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }
  return undefined;
}

function messageMatchesChunkFailure(message: string): boolean {
  return CHUNK_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

function matchesChunkLoadSignature(error: unknown): boolean {
  if (getErrorName(error) === 'ChunkLoadError') {
    return true;
  }
  const message = getErrorMessage(error);
  return message !== undefined && messageMatchesChunkFailure(message);
}

function unwrapRejectionOrReason(error: unknown): unknown {
  if (error === null || typeof error !== 'object') {
    return error;
  }
  const record = error as Record<string, unknown>;
  if ('rejection' in record && record['rejection'] !== undefined) {
    return record['rejection'];
  }
  if ('reason' in record && record['reason'] !== undefined) {
    return record['reason'];
  }
  return error;
}

export function isChunkLoadError(error: unknown): boolean {
  if (matchesChunkLoadSignature(error)) {
    return true;
  }
  const inner = unwrapRejectionOrReason(error);
  if (inner !== error) {
    return matchesChunkLoadSignature(inner);
  }
  return false;
}

export interface ChunkLoadRecoveryDeps {
  reloadGuardWindowMs?: number;
  now?: () => number;
  storage?: Storage;
  /** Router target when recovery was triggered by a failed lazy navigation. */
  targetUrl?: string;
  location?: Pick<Location, 'href' | 'replace' | 'reload'>;
  navigator?: Pick<Navigator, 'serviceWorker'>;
  caches?: CacheStorage;
}

const DEFAULT_RELOAD_GUARD_MS = 60_000;

let recoveryPromise: Promise<'recovering' | 'gave-up'> | null = null;

function readSessionStorage(): Storage | undefined {
  if (typeof globalThis === 'undefined') {
    return undefined;
  }
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

function readLocation(): Pick<Location, 'href' | 'replace' | 'reload'> | undefined {
  if (typeof globalThis === 'undefined' || !('location' in globalThis)) {
    return undefined;
  }
  return globalThis.location as Pick<Location, 'href' | 'replace' | 'reload'>;
}

function readNavigator(): Pick<Navigator, 'serviceWorker'> | undefined {
  if (typeof globalThis === 'undefined' || !('navigator' in globalThis)) {
    return undefined;
  }
  return globalThis.navigator as Pick<Navigator, 'serviceWorker'>;
}

function readCaches(): CacheStorage | undefined {
  if (typeof globalThis === 'undefined' || !('caches' in globalThis)) {
    return undefined;
  }
  const c = (globalThis as { caches?: CacheStorage }).caches;
  return c;
}

/** Clears the cross-reload guard after a route renders successfully. */
export function clearChunkRecoveryFlag(storage?: Storage): void {
  const s = storage ?? readSessionStorage();
  if (s) {
    try {
      s.removeItem(CHUNK_RECOVERY_FLAG_KEY);
    } catch {
      /* ignore */
    }
  }
  recoveryPromise = null;
}

/** Unregisters service workers, clears Cache API, and reloads with a cache-busting query param. */
export async function clearCachesAndReload(
  deps?: ChunkLoadRecoveryDeps,
  timestamp?: number
): Promise<void> {
  const locationRef = deps?.location ?? readLocation();
  const navigatorRef = deps?.navigator ?? readNavigator();
  const cachesRef = deps?.caches ?? readCaches();
  const t = timestamp ?? (deps?.now ?? Date.now)();

  if (
    navigatorRef &&
    'serviceWorker' in navigatorRef &&
    navigatorRef.serviceWorker
  ) {
    try {
      const registrations =
        await navigatorRef.serviceWorker.getRegistrations();
      await Promise.allSettled(
        registrations.map((registration) => registration.unregister())
      );
    } catch {
      /* ignore */
    }
  }

  if (cachesRef) {
    try {
      const keys = await cachesRef.keys();
      await Promise.allSettled(keys.map((key) => cachesRef.delete(key)));
    } catch {
      /* ignore */
    }
  }

  if (locationRef) {
    const url = deps?.targetUrl
      ? new URL(deps.targetUrl, locationRef.href)
      : new URL(locationRef.href);
    const alreadyHasVersion = url.searchParams.has('_v');
    url.searchParams.set('_v', String(t));
    if (alreadyHasVersion && typeof locationRef.reload === 'function') {
      locationRef.reload();
    } else {
      locationRef.replace(url.toString());
    }
  }
}

async function performRecovery(
  deps: ChunkLoadRecoveryDeps | undefined,
  t: number
): Promise<'recovering' | 'gave-up'> {
  const storage = deps?.storage ?? readSessionStorage();

  if (storage) {
    storage.setItem(CHUNK_RECOVERY_FLAG_KEY, String(t));
  }

  await clearCachesAndReload(deps, t);

  return 'recovering';
}

export async function recoverFromChunkLoadError(
  deps?: ChunkLoadRecoveryDeps
): Promise<'recovering' | 'gave-up'> {
  if (recoveryPromise) {
    return recoveryPromise;
  }

  const reloadGuardWindowMs =
    deps?.reloadGuardWindowMs ?? DEFAULT_RELOAD_GUARD_MS;
  const now = deps?.now ?? Date.now;
  const storage = deps?.storage ?? readSessionStorage();
  const t = now();

  if (storage) {
    const raw = storage.getItem(CHUNK_RECOVERY_FLAG_KEY);
    if (raw !== null && raw !== '') {
      const parsed = Number.parseInt(raw, 10);
      if (!Number.isNaN(parsed) && t - parsed < reloadGuardWindowMs) {
        return 'gave-up';
      }
    }
  }

  recoveryPromise = performRecovery(deps, t).finally(() => {
    recoveryPromise = null;
  });
  return recoveryPromise;
}
